/**
 * InvoicesFilters Component
 * 
 * Collapsible filter panel for the invoices list.
 * Extracted from InvoicesList for Single Responsibility (SRP).
 * 
 * Features:
 *   - Customer autocomplete filter
 *   - Status dropdown filter
 *   - Date range filters with presets
 *   - Deadline range filters with presets
 *   - Active filter tags with remove buttons
 */
import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSearch, 
  faChevronUp,
  faFilter,
  faTimes,
} from '@fortawesome/free-solid-svg-icons';

import { CustomerAutocomplete } from '../common';
import {
  DatePreset,
  StatusFilter,
  FilterState,
  statusFilterOptions,
  datePresetOptions,
  getDateRangeForPreset,
} from './filterUtils';

interface InvoicesFiltersProps {
  /** Current filter values (working state) */
  filters: FilterState;
  /** Applied filter values (used for API call) */
  appliedFilters: FilterState;
  /** Whether filters panel is expanded */
  isExpanded: boolean;
  /** Whether any filters are active */
  hasActiveFilters: boolean;
  /** Count of active filters */
  activeFilterCount: number;
  /** Callback to update filter values */
  onFiltersChange: (filters: FilterState) => void;
  /** Callback when Apply button is clicked */
  onApply: () => void;
  /** Callback when Clear button is clicked */
  onClear: () => void;
  /** Callback when filter tag remove is clicked */
  onRemoveFilter: (newFilters: FilterState) => void;
  /** Callback to toggle panel expansion */
  onToggleExpanded: () => void;
}

const InvoicesFilters: React.FC<InvoicesFiltersProps> = ({
  filters,
  appliedFilters,
  isExpanded,
  hasActiveFilters,
  activeFilterCount,
  onFiltersChange,
  onApply,
  onClear,
  onRemoveFilter,
  onToggleExpanded,
}) => {
  /**
   * Generic filter field updater (DRY - replaces 8 individual handlers)
   */
  const updateFilter = <K extends keyof FilterState>(
    field: K,
    value: FilterState[K],
    additionalUpdates?: Partial<FilterState>
  ) => {
    onFiltersChange({ ...filters, [field]: value, ...additionalUpdates });
  };

  /**
   * Handle preset change for date/deadline fields
   * Calculates date range from preset and updates related fields
   */
  const handlePresetChange = (
    presetField: 'datePreset' | 'deadlinePreset',
    fromField: 'dateFrom' | 'deadlineFrom',
    toField: 'dateTo' | 'deadlineTo',
    preset: DatePreset
  ) => {
    if (preset === 'custom') {
      updateFilter(presetField, preset);
    } else {
      const { from, to } = getDateRangeForPreset(preset);
      onFiltersChange({ 
        ...filters, 
        [presetField]: preset, 
        [fromField]: from, 
        [toField]: to 
      });
    }
  };

  return (
    <>
      {/* Filter Toggle Bar */}
      <div className="filter-header" onClick={onToggleExpanded}>
        <button 
          className={`filter-toggle-btn ${hasActiveFilters ? 'has-filters' : ''}`}
          onClick={(e) => {
            e.stopPropagation();
            onToggleExpanded();
          }}
        >
          <FontAwesomeIcon icon={faFilter} />
          <span>Filters</span>
          {activeFilterCount > 0 && (
            <span className="filter-count">{activeFilterCount}</span>
          )}
        </button>
        <div className={`section-toggle ${isExpanded ? '' : 'collapsed'}`}>
          <FontAwesomeIcon icon={faChevronUp} />
        </div>
      </div>

      {/* Filter Bar */}
      {isExpanded && (
        <div className="filter-bar">
          {/* Row 1: Customer, Status */}
          <div className="filter-row">
            <div className="filter-group filter-group-customer">
              <label className="filter-label">Customer</label>
              <CustomerAutocomplete
                value={filters.customer}
                onChange={(customer) => updateFilter('customer', customer)}
              />
            </div>
            <div className="filter-group">
              <label className="filter-label">Status</label>
              <select
                className="form-control form-control-filter"
                value={filters.status}
                onChange={(e) => updateFilter('status', e.target.value as StatusFilter)}
              >
                {statusFilterOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Row 2: Date Fields Group */}
          <div className="filter-field-group">
            <span className="filter-field-group-label">Invoice Date</span>
            <div className="filter-field-group-content">
              <div className="filter-group">
                <label className="filter-label">Preset</label>
                <select
                  className="form-control form-control-filter"
                  value={filters.datePreset}
                  onChange={(e) => handlePresetChange('datePreset', 'dateFrom', 'dateTo', e.target.value as DatePreset)}
                >
                  {datePresetOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div className="filter-group">
                <label className="filter-label">From</label>
                <input
                  type="date"
                  className="form-control form-control-filter"
                  value={filters.dateFrom}
                  onChange={(e) => updateFilter('dateFrom', e.target.value, { datePreset: 'custom' })}
                />
              </div>
              <div className="filter-group">
                <label className="filter-label">To</label>
                <input
                  type="date"
                  className="form-control form-control-filter"
                  value={filters.dateTo}
                  onChange={(e) => updateFilter('dateTo', e.target.value, { datePreset: 'custom' })}
                />
              </div>
            </div>
          </div>

          {/* Row 3: Deadline Fields Group */}
          <div className="filter-field-group">
            <span className="filter-field-group-label">Deadline</span>
            <div className="filter-field-group-content">
              <div className="filter-group">
                <label className="filter-label">Preset</label>
                <select
                  className="form-control form-control-filter"
                  value={filters.deadlinePreset}
                  onChange={(e) => handlePresetChange('deadlinePreset', 'deadlineFrom', 'deadlineTo', e.target.value as DatePreset)}
                >
                  {datePresetOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div className="filter-group">
                <label className="filter-label">From</label>
                <input
                  type="date"
                  className="form-control form-control-filter"
                  value={filters.deadlineFrom}
                  onChange={(e) => updateFilter('deadlineFrom', e.target.value, { deadlinePreset: 'custom' })}
                />
              </div>
              <div className="filter-group">
                <label className="filter-label">To</label>
                <input
                  type="date"
                  className="form-control form-control-filter"
                  value={filters.deadlineTo}
                  onChange={(e) => updateFilter('deadlineTo', e.target.value, { deadlinePreset: 'custom' })}
                />
              </div>
            </div>
          </div>

          {/* Row 4: Actions */}
          <div className="filter-actions-row">
            <button className="action-panel-btn-inline action-panel-btn-inline-sm" onClick={onApply}>
              <span className="action-panel-icon-inline">
                <FontAwesomeIcon icon={faSearch} />
              </span>
              <span className="action-panel-label">Apply</span>
            </button>
            {hasActiveFilters && (
              <button className="action-panel-btn-inline action-panel-btn-inline-sm" onClick={onClear}>
                <span className="action-panel-icon-inline">
                  <FontAwesomeIcon icon={faTimes} />
                </span>
                <span className="action-panel-label">Clear</span>
              </button>
            )}
          </div>

          {/* Active Filters Tags */}
          {hasActiveFilters && (
            <div className="active-filters">
              <span className="active-filters-label">Active filters:</span>
              {appliedFilters.customer && (
                <span className="filter-tag">
                  Customer: {appliedFilters.customer.first_name} {appliedFilters.customer.last_name}
                  <button 
                    className="filter-tag-remove"
                    onClick={() => onRemoveFilter({ ...appliedFilters, customer: null })}
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                </span>
              )}
              {appliedFilters.status !== 'all' && (
                <span className="filter-tag">
                  Status: {statusFilterOptions.find(o => o.value === appliedFilters.status)?.label}
                  <button 
                    className="filter-tag-remove"
                    onClick={() => onRemoveFilter({ ...appliedFilters, status: 'all' as StatusFilter })}
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                </span>
              )}
              {(appliedFilters.dateFrom || appliedFilters.dateTo) && (
                <span className="filter-tag">
                  Date: {appliedFilters.dateFrom || '...'} → {appliedFilters.dateTo || '...'}
                  <button 
                    className="filter-tag-remove"
                    onClick={() => onRemoveFilter({ 
                      ...appliedFilters, 
                      dateFrom: '', 
                      dateTo: '', 
                      datePreset: 'custom' as DatePreset 
                    })}
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                </span>
              )}
              {(appliedFilters.deadlineFrom || appliedFilters.deadlineTo) && (
                <span className="filter-tag">
                  Deadline: {appliedFilters.deadlineFrom || '...'} → {appliedFilters.deadlineTo || '...'}
                  <button 
                    className="filter-tag-remove"
                    onClick={() => onRemoveFilter({ 
                      ...appliedFilters, 
                      deadlineFrom: '', 
                      deadlineTo: '', 
                      deadlinePreset: 'custom' as DatePreset 
                    })}
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                </span>
              )}
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default InvoicesFilters;
